import React, { useEffect, useState } from "react";
import { Button, Input, Select } from "antd";
import { Pagination, CodarxDropDown, CustomersFilters } from "..";
import { useSelector } from "react-redux";
import { TableStyles, StyledTable } from "./style";
import { tableBodyParser } from "utils/_helpers";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import "react-perfect-scrollbar/dist/css/styles.css";
import { useMediaQuery } from "utils/useMediaQuery";
import Skeleton from "react-loading-skeleton";
import { OrderFilters } from "components/tableFilters";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import DraggableHeaderCell from "./DraggableHeaderCell";
import { useColumnOrder, getColumnKey } from "./useColumnOrder";

const options = [
  { value: 10, label: 10 },
  { value: 25, label: 25 },
  { value: 50, label: 50 },
  { value: 100, label: 100 },
  { value: 200, label: 200 },
];

const CodarxTable = ({
  fields,
  tableData,
  searchPlaceHolder,
  hasCustomFilters,
  hasSearch = true,
  totalResults,
  tableTitle,
  disableRowOnClick,
  loading,
  targetField = "id",
  onRowClick = null,
  hasTabs = true,
  hasPagination = true,
  hasHeader = true,
  filter = null,
  isScrollable = true,
  onChange,
  selectable = false,
  selectedKeys = [],
  onSelectedKeysChange = null,
  scrollConfig = null,
  onPageSizeChange = null,
  onPageChange = null,
  onSearch = null,
  onSort = null,
  draggableColumns = true,
  tableId = null,
}) => {
  const colorPalette = useSelector(({ theme }) => theme.palette);
  const { pathname, state } = useLocation();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const parameters = state || Object.fromEntries(searchParams) || {};

  const isTabletOrMobile = useMediaQuery("(max-width: 1200px)");

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Use tableId if provided, otherwise fall back to pathname so each page auto-persists
  const storageKey = draggableColumns ? tableId || pathname : null;
  const { orderedColumns, handleDragEnd, resetOrder, hasCustomOrder } =
    useColumnOrder(storageKey, fields || []);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const draggableFields = draggableColumns
    ? orderedColumns.map((col) => ({
        ...col,
        onHeaderCell: () => ({ dragId: getColumnKey(col) || null }),
      }))
    : fields;

  const columnIds = draggableColumns
    ? orderedColumns.map(getColumnKey).filter(Boolean)
    : [];

  const activeOrdering = parameters.ordering || null;
  const withSortOrder = (cols) =>
    cols.map((col) => {
      if (!col.sorter) return col;
      if (!activeOrdering) return { ...col, sortOrder: null };
      const isDesc = activeOrdering.startsWith("-");
      const fieldName = isDesc ? activeOrdering.slice(1) : activeOrdering;
      return {
        ...col,
        sortOrder:
          col.dataIndex === fieldName ? (isDesc ? "descend" : "ascend") : null,
      };
    });

  const rowSelection = selectable
    ? {
        selectedRowKeys: selectedKeys,
        onChange: (keys) => {
          if (onSelectedKeysChange) onSelectedKeysChange(keys);
        },
        columnWidth: 48,
      }
    : undefined;

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(debouncedSearch), 1000);
    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  useEffect(() => {
    if (searchText) {
      navigate("", { state: { ...parameters, page: 1, search: searchText } });
      onSearch && onSearch(searchText);
    }
  }, [searchText]);

  return (
    <TableStyles isTabletOrMobile={isTabletOrMobile}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {hasTabs && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              className="pr_table_title"
              style={{
                color: colorPalette.text.title,
                textAlign: "center",
                borderBottom: "2px solid " + colorPalette.primary.op_1,
              }}
            >
              {tableTitle}
            </div>
            {draggableColumns && hasCustomOrder && (
              <Button
                type="link"
                size="small"
                onClick={resetOrder}
                style={{ fontSize: 12, marginRight: 8 }}
              >
                ریست ترتیب ستون‌ها
              </Button>
            )}
          </div>
        )}
        {hasSearch && (
          <div
            className="pr_serach"
            style={{
              border: "1px solid " + colorPalette.text.op_7,
              marginLeft: "1rem",
            }}
          >
            <Input
              bordered={false}
              placeholder={searchPlaceHolder}
              className="pr_serach_input"
              defaultValue={
                parameters?.search || searchParams.get("search") || ""
              }
              suffix={<i className="iconly-Search" style={{ scale: "1.5" }} />}
              onChange={(e) => {
                setDebouncedSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  navigate("", {
                    state: { ...parameters, search: searchText, page: 1 },
                  });
                }
              }}
            />
          </div>
        )}
      </div>
      {filter && <>{filter}</>}
      {/* {hasHeader && <OrderFilters />} */}
      {loading && (
        <Skeleton
          style={{
            position: "absolute",
            padding: "0px 16px",
            borderRadius: 0,
            height: filter ? "65vh" : "73vh",
            zIndex: 100,
          }}
          width={"100%"}
        />
      )}
      {!loading && !draggableColumns && (
        <StyledTable
          rowSelection={rowSelection}
          columns={withSortOrder(draggableFields)}
          dataSource={tableBodyParser(tableData)}
          pagination={false}
          colorPalette={colorPalette}
          scroll={
            scrollConfig
              ? scrollConfig
              : isScrollable
                ? { x: 1200, y: filter ? "47vh" : "55vh" }
                : { y: 500 }
          }
          onChange={(p, f, sorter) => {
            if (sorter) {
              if (!sorter.order) {
                if (onSort) {
                  onSort(null);
                } else {
                  const { ordering, ...newParams } = parameters;
                  navigate("", {
                    replace: true,
                    state: { ...newParams, page: 1 },
                  });
                }
              } else if (sorter.order == "ascend") {
                if (onSort) {
                  onSort(sorter.field);
                } else {
                  navigate("", {
                    replace: true,
                    state: { ...parameters, ordering: sorter.field, page: 1 },
                  });
                }
              } else {
                if (onSort) {
                  onSort("-" + sorter.field);
                } else {
                  navigate("", {
                    replace: true,
                    state: {
                      ...parameters,
                      ordering: "-" + sorter.field,
                      page: 1,
                    },
                  });
                }
              }
            }
          }}
          onRow={(record) => {
            const url = `${pathname}/single/${record[targetField]}`;
            return disableRowOnClick
              ? {}
              : {
                  onClick: () => {
                    if (onRowClick) {
                      onRowClick(record);
                    } else {
                      window.open(url, "_blank");
                    }
                  },
                };
          }}
        />
      )}
      {!loading && draggableColumns && (
        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={columnIds}
            strategy={horizontalListSortingStrategy}
          >
            <StyledTable
              components={{ header: { cell: DraggableHeaderCell } }}
              rowSelection={rowSelection}
              columns={withSortOrder(draggableFields)}
              dataSource={tableBodyParser(tableData)}
              pagination={false}
              colorPalette={colorPalette}
              scroll={
                scrollConfig
                  ? scrollConfig
                  : isScrollable
                    ? { x: 1200, y: filter ? "47vh" : "55vh" }
                    : { y: 500 }
              }
              onChange={(p, f, sorter) => {
                if (sorter) {
                  if (!sorter.order) {
                    if (onSort) {
                      onSort(null);
                    } else {
                      const { ordering, ...newParams } = parameters;
                      navigate("", {
                        replace: true,
                        state: { ...newParams, page: 1 },
                      });
                    }
                  } else if (sorter.order == "ascend") {
                    if (onSort) {
                      onSort(sorter.field);
                    } else {
                      navigate("", {
                        replace: true,
                        state: {
                          ...parameters,
                          ordering: sorter.field,
                          page: 1,
                        },
                      });
                    }
                  } else {
                    if (onSort) {
                      onSort("-" + sorter.field);
                    } else {
                      navigate("", {
                        replace: true,
                        state: {
                          ...parameters,
                          ordering: "-" + sorter.field,
                          page: 1,
                        },
                      });
                    }
                  }
                }
              }}
              onRow={(record) => {
                const url = `${pathname}/single/${record[targetField]}`;
                return disableRowOnClick
                  ? {}
                  : {
                      onClick: () => {
                        if (onRowClick) {
                          onRowClick(record);
                        } else {
                          window.open(url, "_blank");
                        }
                      },
                    };
              }}
            />
          </SortableContext>
        </DndContext>
      )}
      {hasPagination && (
        <div
          className="pr_table_footer"
          style={{ flexDirection: isTabletOrMobile ? "column" : "row" }}
        >
          <div
            className="pr_table_pagination"
            style={{ marginBottom: isTabletOrMobile ? "24px" : "0px" }}
          >
            <Pagination
              totalResults={totalResults}
              page={parameters.page ? parameters.page : 1}
              pageSize={parameters.page_size ? parameters.page_size : 10}
              onChange={onPageChange}
            />
          </div>
          <div style={{ margin: "0 1rem" }}>
            تعداد کل اطلاعات: <span>{totalResults}</span>
          </div>
          <div>
            <span
              style={{ color: colorPalette.text.title }}
              className="pr_table_rows"
            >
              تعداد سطرها :
            </span>
            <CodarxDropDown
              options={options}
              currentValue={{
                value: parameters.page_size ? parameters.page_size : 10,
                key: parameters.page_size,
              }}
              type="contained"
              onChange={(newPageSize) => {
                navigate("", {
                  state: { ...parameters, page_size: newPageSize, page: 1 },
                });
                onPageSizeChange && onPageSizeChange(newPageSize);
              }}
            />
          </div>
        </div>
      )}
    </TableStyles>
  );
};

CodarxTable.defaultProps = {
  searchPlaceHolder: "جست و جو محصول",
  hasCustomFilters: true,
  tableTitle: "",
};

export default CodarxTable;
